import type { UserStatus } from '@/lib/types';

interface UserStatusBannerProps {
  status: UserStatus;
  message?: string;
}

export function UserStatusBanner({ status, message }: UserStatusBannerProps) {
  if (status === 'active') {
    return null; // No banner for active users
  }

  const getBannerContent = () => {
    switch (status) {
      case 'invited':
        return {
          icon: (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                clipRule="evenodd"
              />
            </svg>
          ),
          color: 'blue',
          title: 'Waiting for Admin Approval',
          description:
            message || 'Your account request is pending. An admin needs to approve your access before you can use Crewflow.',
          showSettings: false,
        };

      case 'suspended':
        return {
          icon: (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z"
                clipRule="evenodd"
              />
            </svg>
          ),
          color: 'red',
          title: 'Access Suspended',
          description:
            message || 'Your access to Crewflow has been suspended. Please contact your administrator for more information.',
          showSettings: true,
        };

      case 'not_member':
        return {
          icon: (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          ),
          color: 'amber',
          title: 'Not a Team Member',
          description:
            message || "You're not a member of this Crewflow account. Ask your admin to invite you, or sign in to activate a trial.",
          showSettings: true,
        };

      default:
        return null;
    }
  };

  const content = getBannerContent();
  if (!content) return null;

  const colorClasses = {
    blue: {
      bg: 'bg-blue-50 dark:bg-blue-900/20',
      border: 'border-blue-200 dark:border-blue-800',
      icon: 'text-blue-600 dark:text-blue-400',
      title: 'text-blue-800 dark:text-blue-200',
      description: 'text-blue-700 dark:text-blue-300',
      link: 'text-blue-800 dark:text-blue-200 hover:text-blue-900 dark:hover:text-blue-100',
    },
    red: {
      bg: 'bg-red-50 dark:bg-red-900/20',
      border: 'border-red-200 dark:border-red-800',
      icon: 'text-red-600 dark:text-red-400',
      title: 'text-red-800 dark:text-red-200',
      description: 'text-red-700 dark:text-red-300',
      link: 'text-red-800 dark:text-red-200 hover:text-red-900 dark:hover:text-red-100',
    },
    amber: {
      bg: 'bg-amber-50 dark:bg-amber-900/20',
      border: 'border-amber-200 dark:border-amber-800',
      icon: 'text-amber-600 dark:text-amber-400',
      title: 'text-amber-800 dark:text-amber-200',
      description: 'text-amber-700 dark:text-amber-300',
      link: 'text-amber-800 dark:text-amber-200 hover:text-amber-900 dark:hover:text-amber-100',
    },
  };

  const colors = colorClasses[content.color as keyof typeof colorClasses];

  return (
    <div className={`${colors.bg} border-b ${colors.border} p-4`}>
      <div className="flex items-start gap-3">
        <div className={`flex-shrink-0 ${colors.icon}`}>{content.icon}</div>
        <div className="flex-1 min-w-0">
          <h3 className={`text-sm font-medium ${colors.title}`}>{content.title}</h3>
          <p className={`mt-1 text-sm ${colors.description}`}>{content.description}</p>
          {content.showSettings && (
            <button
              onClick={() => window.open('https://app.crewflow.com/settings/team', '_blank')}
              className={`mt-2 text-sm font-medium ${colors.link} underline`}
            >
              Contact Admin →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
